import React, {
  useState,
  useEffect,
  useRef,
  useMemo,
  useLayoutEffect,
} from "react";
import { Typography, message, Modal, Upload, Space, Divider } from "antd";
import {
  CheckCircleFilled,
  ExclamationCircleFilled,
  EnvironmentOutlined,
  EditOutlined,
  LoadingOutlined,
  PlusOutlined,
} from "@ant-design/icons";
import { useParams } from "react-router-dom";
import CustomButton from "../../components/CustomButton";
import { SignaturePad } from "./SignaturePad";
import type { SignaturePadRef } from "./SignaturePad";
import { PDFViewer, useImageCompression } from "@ifa/ui";
import {
  submitOrderSignature,
  getSelectMetadataByCode,
  uploadFile,
  getFileBlob,
} from "@ifa/utils";
import type { SignatureConfigItem, OrderSignatureDTO } from "@ifa/utils";
import { SignatureType, City } from "@ifa/utils";
import { FilePreviewModal } from "../../components/FilePreviewModal";
import { useIOSConfirm } from "../../hooks/useIOSConfirm";

const { Text, Paragraph } = Typography;

// 签名类型数据键映射（用于 signatures 对象）
const SIGNATURE_TYPE_KEYS: Record<string, string> = {
  POLICYHOLDER: "policyholderSignData",
  INSURED: "insuredSignData",
  ADVISOR: "advisorSignData",
  WITNESS: "witnessSignData",
};

const LOCATION_FAILURE_MESSAGES = [
  "定位失敗",
  "瀏覽器不支援定位",
  "定位暫時未返回，請點擊重新獲取",
];

const isSuccessfulLocation = (location?: string) =>
  !!location &&
  !LOCATION_FAILURE_MESSAGES.includes(location) &&
  !location.includes("定位驗證失敗");

// 从元数据获取签名类型名称映射
const getSignatureTypeNames = (): Record<string, string> => {
  const metadata = getSelectMetadataByCode("SIGNATURE_TYPE");
  const names: Record<string, string> = {};

  if (metadata?.items) {
    metadata.items.forEach((item) => {
      if (item.key && item.name) {
        names[item.key] = item.name;
      }
    });
  }

  return names;
};

interface OrderSignatureFormProps {
  signatures?: { [key: string]: string };
  ossSignatures?: { [key: string]: string }; // OSS 路径（用于提交未修改的签名）
  onSignatureChange?: (role: string, dataUrl: string | null) => void;
  onSubmitOrder?: () => void;
  onSignStateChange?: (state: {
    canSign: boolean;
    countdown: number;
    isLastDoc: boolean;
    isCurrentDocRead: boolean;
    allDocsRead: boolean;
    isPdfLoaded: boolean;
  }) => void;
  readOnly?: boolean;
  scrollContainerRef?: React.RefObject<HTMLDivElement>;
  location?: string;
  locationLoading?: boolean;
  onGetLocation?: (useHighAccuracy?: boolean) => void;

  // Props from parent
  documents: SignatureConfigItem[];
  activeDocKey: string;
  onActiveDocChange: (key: string) => void;
  readDocs: Set<string>;
  onReadDoc: (key: string) => void;
  signatureTypes?: string[]; // 签名类型列表（从接口获取）
  existingPhoto?: string; // 已上传的照片URL（用于显示）
  existingOssPhoto?: string; // 已上传的照片OSS路径（用于提交）
  orderNo?: string; // 订单号（用于文件上传）
  signatureNames?: { [key: string]: string }; // 各签名类型对应的姓名（用于背景显示）
}

export interface OrderSignatureFormRef {
  openSignModal: () => void;
  handleSubmit: () => void;
  nextDoc: () => void;
  prevDoc: () => void;
}

export const OrderSignatureForm = React.forwardRef<
  OrderSignatureFormRef,
  OrderSignatureFormProps
>(
  (
    {
      signatures = {},
      ossSignatures = {}, // OSS 路径（用于提交未修改的签名）
      onSignatureChange,
      onSubmitOrder,
      onSignStateChange,
      readOnly = false,
      location,
      locationLoading,
      onGetLocation,
      documents = [],
      activeDocKey,
      onActiveDocChange,
      readDocs = new Set(),
      onReadDoc,
      signatureTypes = [], // 默认空数组
      existingPhoto = "", // 已上传的照片URL（用于显示）
      existingOssPhoto = "", // 已上传的照片OSS路径（用于提交）
      orderNo = "", // 订单号
      signatureNames = {}, // 各签名类型对应的姓名
    },
    ref,
  ) => {
    const { orderId } = useParams();
    // const navigate = useNavigate();
    const { confirm } = useIOSConfirm();

    // 使用图片压缩 Hook
    const { handleBeforeUpload } = useImageCompression({
      confirm,
    });

    // 从元数据获取签名类型名称映射（只计算一次）
    const signatureTypeNames = useMemo(() => getSignatureTypeNames(), []);

    const [previewUrl, setPreviewUrl] = useState<string>("");

    const [isModalOpen, setIsModalOpen] = useState(false);
    const signaturePadRef = useRef<SignaturePadRef>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    // Store the active document key at the time of initialization to detect stale checks
    const activeDocKeyRef = useRef<string>(activeDocKey);
    const [countdown, setCountdown] = useState(20);
    const [isScrolledToBottom, setIsScrolledToBottom] = useState(false);
    const [isPdfLoaded, setIsPdfLoaded] = useState(false);
    // Add a state to block scroll calculation temporarily
    const [canCheckScroll, setCanCheckScroll] = useState(false);
    // Add a flag to prevent any marking during document switching
    const [isDocumentSwitching, setIsDocumentSwitching] = useState(false);

    const [photoOssPath, setPhotoOssPath] = useState<string>(""); // 存储上传后的OSS路径
    const [isPhotoUploading, setIsPhotoUploading] = useState(false); // 照片上传中状态
    const [photoDisplayUrl, setPhotoDisplayUrl] = useState<string>(""); // 当前显示的照片 URL
    const [modifiedSignatures, setModifiedSignatures] = useState<Set<string>>(
      new Set(),
    ); // 追踪哪些签名被重新签署
    const [signingRole, setSigningRole] = useState<string | null>(null);
    const [tempSignature, setTempSignature] = useState<string>("");
    const [isSignatureReady, setIsSignatureReady] = useState(false);

    // 照片预览状态
    const [photoPreviewOpen, setPhotoPreviewOpen] = useState(false);
    const [photoPreviewFile, setPhotoPreviewFile] = useState<{
      url: string;
      name: string;
    }>();
    const [scrollProgress, setScrollProgress] = useState(0); // 滚动进度 0-100

    const activeDoc = useMemo(
      () =>
        documents.find((d) => (d.key || d.formType || d.type) === activeDocKey),
      [documents, activeDocKey],
    );
    const activeDocFileUrl = (activeDoc as any)?._fileUrl || "";

    // 处理已有照片的回显
    useEffect(() => {
      const loadExistingPhoto = async () => {
        console.log("📸 尝试回显照片:", { existingPhoto, existingOssPhoto });

        if (existingOssPhoto && existingOssPhoto.trim() !== "") {
          try {
            // 即使有 existingPhoto，也优先使用 existingOssPhoto 获取最新的 URL，防止 URL 过期
            // 除非 existingPhoto 是 base64 数据
            if (existingPhoto && existingPhoto.startsWith("data:")) {
              setPhotoDisplayUrl(existingPhoto);
              setPhotoOssPath(existingOssPhoto);
              console.log("✅ 照片回显（Base64）");
            } else {
              // 通过 OSS 路径获取最新的预览 URL
              console.log("🔄 正在通过 OSS 路径获取照片预览 URL...");
              const { url: previewUrl } = await getFileBlob(existingOssPhoto);
              setPhotoDisplayUrl(previewUrl);
              setPhotoOssPath(existingOssPhoto);
              console.log(
                "✅ 照片回显成功（OSS路径）:",
                existingOssPhoto,
                "预览URL:",
                previewUrl,
              );
            }
          } catch (error) {
            console.error("❌ 获取照片预览失败:", error);
            // 如果获取失败，但有 existingPhoto，尝试降级使用它
            if (existingPhoto && existingPhoto.trim() !== "") {
              setPhotoDisplayUrl(existingPhoto);
              setPhotoOssPath(existingOssPhoto);
              console.log("⚠️ 降级使用 existingPhoto 回显");
            }
          }
        } else if (existingPhoto && existingPhoto.trim() !== "") {
          // 如果只有 existingPhoto（向后兼容或没有 OSS 路径）
          setPhotoDisplayUrl(existingPhoto);
          setPhotoOssPath(existingOssPhoto || "");
          console.log("✅ 照片回显（仅 URL）:", existingPhoto);
        } else {
          setPhotoDisplayUrl("");
          setPhotoOssPath("");
        }
      };

      loadExistingPhoto();
    }, [existingPhoto, existingOssPhoto]);

    // 注意：不需要在 signatures 变化时重置 modifiedSignatures
    // modifiedSignatures 的初始值已经是空 Set，只有用户主动签名或删除签名时才会修改
    // 如果在每次 signatures 变化时都重置，会导致用户重新签名后的修改标记被清空

    // ============================================
    // Document Reading Verification Logic
    // ============================================
    // Flow:
    // 1. User switches document → Reset scroll, countdown, and flags
    // 2. PDF loads → Wait 1s → Enable scroll detection
    // 3. User scrolls to bottom → Clear countdown immediately → Mark as read
    // 4. OR countdown reaches 0 → Check if scrolled → Mark as read
    // 5. Non-scrollable content → Auto-mark as read when countdown reaches 0

    // Reset all states when document changes
    // Use useLayoutEffect to ensure synchronous state reset BEFORE any other effects run
    useLayoutEffect(() => {
      console.log("🔄 文档切换，重置状态（同步）:", {
        oldDoc: previewUrl ? "有文档" : "无文档",
        newDoc: activeDocKey,
      });

      // CRITICAL: Update ref FIRST to ensure all checks use the new document key
      activeDocKeyRef.current = activeDocKey;

      // CRITICAL: Set switching flag FIRST to block any marking operations
      setIsDocumentSwitching(true);

      // Reset scroll position
      const target = containerRef.current;
      if (target) {
        target.scrollTop = 0;
      }

      // Set countdown based on read status
      // This prevents race conditions where the old countdown triggers marking the new doc as read
      const isAlreadyRead = readDocs.has(activeDocKey);
      const newCountdown = readOnly || isAlreadyRead ? 0 : 20;
      setCountdown(newCountdown);

      // Reset all reading-related states
      setIsScrolledToBottom(false);
      setIsPdfLoaded(false);
      setCanCheckScroll(false);
      setScrollProgress(isAlreadyRead ? 100 : 0);

      console.log("📝 重置完成（同步）:", {
        activeDocKey,
        isAlreadyRead,
        newCountdown,
        readDocsSize: readDocs.size,
        isScrolledToBottom: false,
        isPdfLoaded: false,
        scrollProgress: 0,
      });

      // Set preview URL for the active document
      if (activeDoc) {
        const fileUrl = (activeDoc as any)._fileUrl || "";
        setPreviewUrl(fileUrl);
      } else {
        setPreviewUrl("");
      }

      // Clear switching flag after a brief delay to ensure all state updates have settled
      const timer = setTimeout(() => {
        setIsDocumentSwitching(false);
        console.log("✅ 文档切换完成，解除阻止标记");
      }, 100);

      return () => clearTimeout(timer);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeDocKey, readOnly]); // Intentionally exclude readDocs to prevent scroll reset when marking as read

    // Keep preview URL in sync even when the active document key does not change
    // (e.g. parent refreshes the same PDF and replaces _fileUrl after re-fetching).
    useEffect(() => {
      setPreviewUrl(activeDocFileUrl);
      setIsPdfLoaded(false);
      setCanCheckScroll(false);
    }, [activeDocFileUrl]);

    // Enable scroll check only after PDF is loaded (with 1s delay)
    useEffect(() => {
      console.log("⚠️ 滚动检查 useEffect 触发", {
        isPdfLoaded,
        isDocumentSwitching,
        activeDocKey,
        activeDocKeyRefCurrent: activeDocKeyRef.current,
      });

      // CRITICAL: Only enable if PDF is loaded AND not currently switching documents
      if (!isPdfLoaded || isDocumentSwitching) {
        console.log("🚫 不满足启用滚动检查的前置条件");
        return;
      }

      console.log("⏰ PDF 已加载，准备启用滚动检查", {
        activeDocKey,
        activeDocKeyRefCurrent: activeDocKeyRef.current,
      });

      // CRITICAL: Verify this is for the current document, not a stale effect from previous document
      if (activeDocKeyRef.current !== activeDocKey) {
        console.log("🚫 阻止启用滚动检查: 这是旧文档的 PDF 加载事件", {
          activeDocKey,
          activeDocKeyRefCurrent: activeDocKeyRef.current,
        });
        return;
      }

      // ⚠️ 新增: 验证当前 previewUrl 是否真的属于当前文档
      // 防止旧文档的 PDF 加载完成触发新文档的滚动检查
      const currentDocFileUrl = activeDoc
        ? (activeDoc as any)._fileUrl || ""
        : "";
      if (previewUrl !== currentDocFileUrl) {
        console.log("🚫 阻止启用滚动检查: previewUrl 不匹配当前文档", {
          activeDocKey,
          previewUrl,
          currentDocFileUrl,
        });
        return;
      }

      console.log("⏰ PDF 已加载（当前文档），1 秒后启用滚动检查");

      const timer = setTimeout(() => {
        // Triple-check: Verify document hasn't switched and PDF is still loaded
        if (
          activeDocKeyRef.current === activeDocKey &&
          isPdfLoaded &&
          !isDocumentSwitching
        ) {
          setCanCheckScroll(true);
          console.log("✅ 滚动检查已启用");
        } else {
          console.log("🚫 取消启用滚动检查: 状态已变化", {
            activeDocKey,
            activeDocKeyRefCurrent: activeDocKeyRef.current,
            isPdfLoaded,
            isDocumentSwitching,
          });
        }
      }, 1000);

      return () => {
        clearTimeout(timer);
        console.log("🧹 清理滚动检查定时器");
      };
    }, [isPdfLoaded, activeDocKey, isDocumentSwitching, previewUrl, activeDoc]); // 新增 previewUrl 和 activeDoc 依赖

    // Countdown timer (only runs when PDF is loaded and not in readonly mode)
    useEffect(() => {
      if (readOnly) return;
      if (!isPdfLoaded) return;

      let timer: ReturnType<typeof setTimeout>;
      if (countdown > 0) {
        timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      }
      return () => clearTimeout(timer);
    }, [countdown, readOnly, isPdfLoaded]);

    // Mark as read only if countdown is 0 AND scrolled to bottom
    useEffect(() => {
      if (readOnly) return;
      if (activeDocKey && !readDocs.has(activeDocKey)) {
        // CRITICAL: Verify this check is for the current document
        // Prevents stale checks from marking the wrong document as read
        if (activeDocKeyRef.current !== activeDocKey) {
          console.log("🚫 阻止标记为已读: 文档密钥不匹配（陈旧检查）", {
            activeDocKey,
            activeDocKeyRefCurrent: activeDocKeyRef.current,
          });
          return;
        }

        // CRITICAL: Block marking during document switching
        if (isDocumentSwitching) {
          console.log("🚫 阻止标记为已读: 文档正在切换中", {
            activeDocKey,
            isDocumentSwitching,
          });
          return;
        }

        // 额外保护：确保 PDF 已加载且 canCheckScroll 已启用
        // 防止在文档切换瞬间（状态未完全重置）就标记为已读
        if (!isPdfLoaded || !canCheckScroll) {
          console.log("🚫 阻止标记为已读: PDF 未加载或滚动检查未启用", {
            activeDocKey,
            isPdfLoaded,
            canCheckScroll,
          });
          return;
        }

        // ⚠️ CRITICAL: 验证 previewUrl 是否属于当前文档
        // 这是最关键的检查,防止使用旧文档的滚动状态标记新文档
        const currentDocFileUrl = activeDoc
          ? (activeDoc as any)._fileUrl || ""
          : "";
        if (previewUrl !== currentDocFileUrl) {
          console.log("🚫 阻止标记为已读: previewUrl 不匹配当前文档", {
            activeDocKey,
            previewUrl,
            currentDocFileUrl,
          });
          return;
        }

        console.log("🔍 检查是否标记为已读:", {
          activeDocKey,
          activeDocKeyRefCurrent: activeDocKeyRef.current,
          countdown,
          isScrolledToBottom,
          isPdfLoaded,
          canCheckScroll,
          isDocumentSwitching,
          previewUrl,
          currentDocFileUrl,
          shouldMarkAsRead: countdown === 0 && isScrolledToBottom,
        });

        // ✅ 两种情况都标记为已读:
        // 1. 倒计时期间滑动到底部 (countdown 会被清空为 0)
        // 2. 倒计时自然结束 (无论是否滑动到底部)
        if (countdown === 0) {
          console.log("✅ 标记文档为已读:", activeDocKey, {
            reason: isScrolledToBottom ? "用户已滑动到底部" : "倒计时结束",
          });
          onReadDoc(activeDocKey);
        }
      }
    }, [
      countdown,
      isScrolledToBottom,
      activeDocKey,
      readDocs,
      readOnly,
      onReadDoc,
      isPdfLoaded,
      canCheckScroll,
      isDocumentSwitching,
      previewUrl,
      activeDoc,
    ]);

    // Clear countdown when scrolled to bottom
    useEffect(() => {
      if (readOnly) return;
      if (isScrolledToBottom && countdown > 0) {
        setCountdown(0);
      }
    }, [isScrolledToBottom, countdown, readOnly]);

    // Check for non-scrollable content after PDF loads and canCheckScroll is enabled
    // This handles cases where content is too short to require scrolling
    useEffect(() => {
      if (!canCheckScroll || !isPdfLoaded || readOnly) return;

      // CRITICAL: Verify this is for the current document
      if (activeDocKeyRef.current !== activeDocKey) {
        console.log("🚫 跳过不可滚动内容检查: 文档密钥不匹配", {
          activeDocKey,
          activeDocKeyRefCurrent: activeDocKeyRef.current,
        });
        return;
      }

      const target = containerRef.current;
      if (target) {
        const isScrollable = target.scrollHeight > target.clientHeight + 10;

        if (!isScrollable && !readDocs.has(activeDocKey)) {
          // Content fits in viewport, automatically set progress to 100%
          setScrollProgress(100);
          console.log("📏 内容无需滚动，自动设置进度为 100%");

          // When countdown reaches 0, automatically mark as scrolled to bottom
          if (countdown === 0) {
            console.log("⏱️ 倒计时结束 + 内容无需滚动 → 自动标记为已读");
            setIsScrolledToBottom(true);
          }
        }
      }
    }, [
      canCheckScroll,
      isPdfLoaded,
      readOnly,
      countdown,
      activeDocKey,
      readDocs,
    ]);

    // ❌ 移除：这个 useEffect 会在 canCheckScroll 变为 true 时调用 checkScroll，
    // 但此时 checkScroll 可能捕获了旧的闭包值（因为它的依赖项没有变化）
    // useEffect(() => {
    //   if (canCheckScroll && isPdfLoaded) {
    //     checkScroll();
    //   }
    // }, [canCheckScroll, isPdfLoaded, checkScroll]);

    // ❌ 移除：PDF 加载后立即检查会导致使用旧状态
    // 现在完全依赖 canCheckScroll 的 1 秒延迟机制
    // useEffect(() => {
    //   if (readOnly) return;
    //   if (!isPdfLoaded) return;
    //   checkScroll();
    // }, [readOnly, isPdfLoaded, checkScroll]);

    useEffect(() => {
      const currentIndex = documents.findIndex(
        (doc) => (doc.key || doc.formType || doc.type) === activeDocKey,
      );
      const isLastDoc =
        currentIndex === documents.length - 1 && currentIndex !== -1;
      const isCurrentDocRead = readOnly || readDocs.has(activeDocKey);
      const allDocsRead = documents.every(
        (doc) => readOnly || readDocs.has(doc.key || doc.formType || doc.type),
      );
      const canSign = isLastDoc && isCurrentDocRead && allDocsRead;

      console.log("📋 状态更新:", {
        activeDocKey,
        currentIndex,
        isLastDoc,
        isCurrentDocRead,
        allDocsRead,
        canSign,
        countdown,
        isPdfLoaded,
        isScrolledToBottom,
      });

      onSignStateChange?.({
        canSign,
        countdown,
        isLastDoc,
        isCurrentDocRead,
        allDocsRead,
        isPdfLoaded,
      });
    }, [
      activeDocKey,
      documents,
      readDocs,
      countdown,
      onSignStateChange,
      readOnly,
      isPdfLoaded,
      isScrolledToBottom,
    ]);

    const getDocKey = (doc: SignatureConfigItem) =>
      doc.key || doc.formType || doc.type || "";

    const handleNextDoc = () => {
      if (!readOnly && !readDocs.has(activeDocKey)) {
        message.warning("請先閱讀完當前文件");
        return;
      }
      const currentIndex = documents.findIndex(
        (doc) => getDocKey(doc) === activeDocKey,
      );
      if (currentIndex < documents.length - 1) {
        const nextDoc = documents[currentIndex + 1];
        onActiveDocChange(getDocKey(nextDoc));
      }
    };

    const handlePrevDoc = () => {
      const currentIndex = documents.findIndex(
        (doc) => getDocKey(doc) === activeDocKey,
      );
      if (currentIndex > 0) {
        const prevDoc = documents[currentIndex - 1];
        onActiveDocChange(getDocKey(prevDoc));
      }
    };

    const handleOpenSignModal = () => {
      const allRead = documents.every((doc) => readDocs.has(getDocKey(doc)));
      if (!allRead && !readOnly) {
        message.warning("請先閱讀完所有文件");
        return;
      }
      setIsModalOpen(true);
    };

    const handleOpenSignaturePad = (type: string) => {
      setSigningRole(type);
      const dataKey =
        SIGNATURE_TYPE_KEYS[type] || `${type.toLowerCase()}SignData`;
      const currentSignature = signatures[dataKey] || "";
      setTempSignature(currentSignature);
      setIsSignatureReady(!!currentSignature);
    };

    const handleSaveSignature = (dataUrl: string) => {
      if (signingRole) {
        const dataKey =
          SIGNATURE_TYPE_KEYS[signingRole] ||
          `${signingRole.toLowerCase()}SignData`;
        const currentSignature = signatures[dataKey] || "";

        if (currentSignature && dataUrl === currentSignature) {
          message.success("簽署成功");
          setSigningRole(null);
          setIsSignatureReady(false);
          return;
        }

        onSignatureChange?.(dataKey, dataUrl);

        // 标记该签名为已修改（重新签署）
        setModifiedSignatures((prev) => {
          const newSet = new Set(prev);
          newSet.add(dataKey);
          return newSet;
        });

        message.success("簽署成功");
        setSigningRole(null);
        setIsSignatureReady(false);
      }
    };

    const handleSubmit = async () => {
      if (!orderId) {
        message.error("訂單ID丟失");
        return;
      }

      // 验证定位：签署页需与父页面保持同一成功判定
      if (!isSuccessfulLocation(location)) {
        message.error("請先完成定位驗證");
        return;
      }

      try {
        const signatureList = [];

        // 根据 signatureTypes 动态收集所有签名
        for (const type of signatureTypes) {
          const dataKey =
            SIGNATURE_TYPE_KEYS[type] || `${type.toLowerCase()}SignData`;
          const signatureData = signatures[dataKey];
          const ossSignatureData = ossSignatures[dataKey];

          if (signatureData) {
            // 判断该签名是否被重新签署
            const isModified = modifiedSignatures.has(dataKey);

            if (isModified) {
              signatureList.push({
                signature: signatureData,
                type: type as SignatureType,
              });
              continue;
            }

            if (!ossSignatureData) {
              const displayName = signatureTypeNames[type] || type;
              message.error(`${displayName}缺少可提交的簽名數據，請重新簽署後再提交`);
              return;
            }

            signatureList.push({
              signature: ossSignatureData,
              type: type as SignatureType,
            });
          }
        }

        if (signatureList.length === 0) {
          message.error("未檢測到簽名數據");
          return;
        }

        // 使用已上传的照片OSS路径，如果用户没有重新上传，则使用原有的 OSS 路径
        const photoToSubmit = photoOssPath || existingOssPhoto || "";
        console.log("photoToSubmit", photoToSubmit);
        console.log("photoOssPath", photoOssPath);
        console.log("existingOssPhoto", existingOssPhoto);
        const payload: OrderSignatureDTO = {
          orderId: parseInt(orderId, 10),
          signatures: signatureList,
          city: City.HongKong,
          photo: photoToSubmit,
        };

        await submitOrderSignature(payload);

        message.success("訂單簽署提交成功！");
        setIsModalOpen(false);
        onSubmitOrder?.();

        // if (orderId) {
        //   try {
        //     await confirmOrderSigned(parseInt(orderId, 10));
        //     navigate(`/orders/${orderId}/success`);
        //   } catch (error) {
        //     console.error("Failed to confirm order:", error);
        //     message.error("訂單提交失敗");
        //   }
        // } else {
        //   const scrollY = window.scrollY;
        //   setIsModalOpen(false);
        //   // Restore scroll position to prevent jumping to top
        //   setTimeout(() => window.scrollTo(0, scrollY), 0);

        //   onSubmitOrder?.();
        // }
      } catch (error) {
        console.log(error);
        message.error("提交簽署失敗");
      }
    };

    const renderTopBar = () => {
      return (
        <div
          style={{
            position: "sticky",
            top: 0,
            zIndex: 10,
            background: "rgba(255,255,255,0.95)",
            borderBottom: "1px solid #f0f0f0",
            backdropFilter: "blur(4px)",
          }}
        >
          {/* 文件标题栏 */}
          <div
            style={{
              padding: "12px 18px",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              position: "relative",
            }}
          >
            {/* 文件名显示 */}
            <div style={{ fontSize: 16, fontWeight: 500, color: "#333" }}>
              {activeDoc?.name || activeDoc?.formType || "文件"}
            </div>
            {/* Version 显示 */}
            {activeDoc?.version && (
              <div
                style={{
                  position: "absolute",
                  right: 24,
                  bottom: 8,
                  fontSize: 12,
                  color: "#999",
                }}
              >
                {activeDoc.version}
              </div>
            )}
          </div>

          {/* 滚动进度条 */}
          {!readOnly && (
            <div
              style={{
                position: "relative",
                height: 3,
                background: "#f0f0f0",
                overflow: "hidden",
              }}
            >
              <div
                style={{
                  position: "absolute",
                  left: 0,
                  top: 0,
                  height: "100%",
                  width: `${scrollProgress}%`,
                  background: scrollProgress === 100 ? "#52c41a" : "var(--theme-primary)",
                  transition: "width 0.3s ease, background 0.3s ease",
                }}
              />
            </div>
          )}
        </div>
      );
    };

    const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
      if (!canCheckScroll) {
        return;
      }

      const target = e.currentTarget;

      // Check if content is scrollable
      const isScrollable = target.scrollHeight > target.clientHeight + 10;

      if (!isScrollable) {
        // Content fits in viewport, no scrolling needed
        // Don't automatically mark as scrolled to bottom
        setScrollProgress(100); // 不可滚动时进度为 100%
        return;
      }

      // Calculate scroll progress (0-100)
      const scrollTop = target.scrollTop;
      const scrollHeight = target.scrollHeight;
      const clientHeight = target.clientHeight;
      const maxScroll = scrollHeight - clientHeight;
      const progress =
        maxScroll > 0
          ? Math.min(100, Math.round((scrollTop / maxScroll) * 100))
          : 0;

      setScrollProgress(progress);
      console.log(
        `📊 滚動進度: ${progress}% (scrollTop: ${scrollTop.toFixed(0)}, maxScroll: ${maxScroll.toFixed(0)})`,
      );

      // Strict bottom check with minimal tolerance (5px for floating point errors)
      const distanceFromBottom =
        target.scrollHeight - target.scrollTop - target.clientHeight;
      const isBottom = distanceFromBottom <= 5;

      setIsScrolledToBottom(isBottom);
    };

    const renderDocumentContent = () => (
      <div
        style={{
          flex: 1,
          overflowY: readOnly ? "visible" : "auto",
          overflowX: "hidden",
          padding: 12,
          background: "#fff",
          WebkitOverflowScrolling: "touch",
          minHeight: 0,
          ...(readOnly ? {} : { maxHeight: "calc(100vh - 200px)" }), // Only constrain height in edit mode
        }}
        onScroll={handleScroll}
        ref={containerRef}
      >
        <div
          style={{
            width: "100%",
            minHeight: 600,
            background: "#f5f5f5",
            display: "flex",
            justifyContent: "center",
            alignItems: "flex-start",
            border: "1px solid #eee",
            position: "relative",
          }}
        >
          {previewUrl ? (
            <>
              {/* 在 PDF 加载完成前显示加载提示 */}
              {!isPdfLoaded && (
                <div
                  style={{
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    transform: "translate(-50%, -50%)",
                    textAlign: "center",
                    zIndex: 10,
                    background: "rgba(255, 255, 255, 0.95)",
                    padding: "24px 32px",
                    borderRadius: "8px",
                    boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                  }}
                >
                  <LoadingOutlined
                    style={{ fontSize: 32, color: "var(--theme-primary)", marginBottom: 16 }}
                  />
                  <div style={{ fontSize: 16, color: "#333", fontWeight: 500 }}>
                    正在載入檔案...
                  </div>
                  <div style={{ fontSize: 14, color: "#999", marginTop: 8 }}>
                    請稍候
                  </div>
                </div>
              )}
              <div
                style={{
                  width: "100%",
                  display: "flex",
                  justifyContent: "center",
                }}
              >
                <PDFViewer
                  key={previewUrl}
                  file={previewUrl}
                  mode="scroll"
                  onLoadSuccess={() => {
                    // CRITICAL: Only set isPdfLoaded if this is still the current document
                    // Prevents stale PDF load callbacks from old documents
                    const currentDocFileUrl = activeDoc
                      ? (activeDoc as any)._fileUrl || ""
                      : "";
                    if (
                      previewUrl === currentDocFileUrl &&
                      activeDocKeyRef.current === activeDocKey
                    ) {
                      setIsPdfLoaded(true);
                      console.log("✅ PDF 渲染完成（当前文档）", {
                        activeDocKey,
                        previewUrl,
                      });
                    } else {
                      console.log(
                        "🚫 忽略 PDF 渲染完成: 这是旧文档的加载回调",
                        {
                          activeDocKey,
                          activeDocKeyRefCurrent: activeDocKeyRef.current,
                          previewUrl,
                          currentDocFileUrl,
                        },
                      );
                    }
                  }}
                  onLoadError={(error) => {
                    console.error("❌ PDF 渲染失敗:", error);
                  }}
                />
              </div>
            </>
          ) : (
            <div style={{ textAlign: "center", padding: 40 }}>
              <Text type="secondary">暫無預覽檔案</Text>
            </div>
          )}
        </div>

        {!readOnly && (
          <>
            <Divider />
            <Paragraph>
              請仔細閱讀上述條款。點擊右下角按鈕開始簽署流程。
            </Paragraph>
          </>
        )}
      </div>
    );

    // Expose methods via ref
    React.useImperativeHandle(ref, () => ({
      openSignModal: handleOpenSignModal,
      handleSubmit: handleSubmit,
      nextDoc: handleNextDoc,
      prevDoc: handlePrevDoc,
    }));

    return (
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          height: "100%",
          overflow: "hidden",
          background: "#fff",
          borderRadius: 8,
        }}
      >
        {renderTopBar()}
        {renderDocumentContent()}

        {/* Signature Process Modal */}
        <Modal
          open={isModalOpen}
          onCancel={() => setIsModalOpen(false)}
          footer={null}
          width={600}
          centered
          destroyOnHidden
          maskClosable={false}
          title={null}
          styles={{
            body: {
              maxHeight: "70vh",
              overflowY: "auto",
              padding: "0 24px 24px",

              scrollbarWidth: "none", // Firefox
              msOverflowStyle: "none", // IE/Edge
              // Safari/Chrome 通过伪元素隐藏滚动条
            },
          }}
          className="signature-modal-no-scrollbar"
        >
          {/* Step 1: Location */}
          <div
            className="step-row"
            style={{ display: "flex", margin: "32px 0" }}
          >
            <div
              style={{
                marginRight: 16,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
              }}
            >
              {locationLoading ? (
                <LoadingOutlined style={{ fontSize: 24, color: "var(--theme-primary)" }} />
              ) : isSuccessfulLocation(location) ? (
                <CheckCircleFilled style={{ fontSize: 24, color: "var(--theme-primary)" }} />

              ) : location?.includes("定位驗證失敗") ? (
                <ExclamationCircleFilled
                  style={{ fontSize: 24, color: "#ff4d4f" }}
                />
              ) : (
                <ExclamationCircleFilled
                  style={{ fontSize: 24, color: "#faad14" }}
                />
              )}
              <div
                style={{
                  width: 1,
                  background: "#e8e8e8",
                  flex: 1,
                  marginTop: 8,
                }}
              />
            </div>
            <div style={{ flex: 1 }}>
              <div
                style={{
                  fontSize: 16,
                  fontWeight: 500,
                  marginBottom: 12,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                01 定位校驗
                {locationLoading && (
                  <span style={{ fontSize: 14, color: "#999", marginLeft: 8 }}>
                    正在獲取定位...
                  </span>
                )}
                {!locationLoading && isSuccessfulLocation(location) && (
                  <span
                    style={{ color: "#1890ff", fontSize: 14, marginLeft: 8 }}
                  >
                    <EnvironmentOutlined /> {location}
                  </span>
                )}
                {!locationLoading && location?.includes("定位驗證失敗") && (
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                      marginTop: 4,
                    }}
                  >
                    <span style={{ color: "#ff4d4f", fontSize: 14 }}>
                      {location}
                    </span>
                  </div>
                )}
                {!locationLoading &&
                  (!location ||
                    (!isSuccessfulLocation(location) &&
                      !location.includes("定位驗證失敗"))) && (
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 8,
                        marginTop: 4,
                      }}
                    >
                      <span style={{ color: "#ff4d4f", fontSize: 14 }}>
                        {location || "定位未獲取"}
                      </span>
                      <CustomButton
                        height="24px"
                        bgColor="transparent"
                        color="#1890ff"
                        onClick={() => onGetLocation?.()}
                        style={{ padding: 0, fontSize: "14px" }}
                      >
                        重新獲取
                      </CustomButton>
                    </div>
                  )}
              </div>
            </div>
          </div>

          {/* Step 2: Photo */}
          <div
            className="step-row"
            style={{ display: "flex", margin: "32px 0" }}
          >
            <div
              style={{
                marginRight: 16,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
              }}
            >
              {photoDisplayUrl ? (
                <CheckCircleFilled style={{ fontSize: 24, color: "var(--theme-primary)" }} />
              ) : (
                <div
                  style={{
                    width: 24,
                    height: 24,
                    borderRadius: "50%",
                    border: "2px solid var(--theme-primary)",
                    color: "var(--theme-primary)",
                    textAlign: "center",
                    lineHeight: "20px",
                    fontWeight: "bold",
                  }}
                >
                  2
                </div>
              )}
              <div
                style={{
                  width: 1,
                  background: "#e8e8e8",
                  flex: 1,
                  marginTop: 8,
                }}
              />
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 16, fontWeight: 500, marginBottom: 12 }}>
                02 請拍攝您與您客戶的合照上載
              </div>
              {/* 自定义图片显示区域 */}
              {photoDisplayUrl ? (
                <div
                  style={{
                    position: "relative",
                    display: "inline-block",
                    marginBottom: 12,
                  }}
                >
                  <img
                    src={photoDisplayUrl}
                    alt="客戶合照"
                    style={{
                      width: 160,
                      height: 160,
                      objectFit: "cover",
                      borderRadius: 8,
                      border: "1px solid #d9d9d9",
                      cursor: "pointer",
                    }}
                    onClick={() => {
                      setPhotoPreviewFile({
                        url: photoDisplayUrl,
                        name: "客戶合照",
                      });
                      setPhotoPreviewOpen(true);
                    }}
                  />
                  <div
                    onClick={() => {
                      setPhotoDisplayUrl("");
                      setPhotoOssPath("");
                    }}
                    style={{
                      position: "absolute",
                      top: -10,
                      right: -10,
                      background: "#ff4d4f",
                      color: "white",
                      borderRadius: "50%",
                      width: 24,
                      height: 24,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      cursor: "pointer",
                      fontSize: 12,
                      fontWeight: "bold",
                    }}
                  >
                    ×
                  </div>
                </div>
              ) : (
                <Upload
                  accept="image/jpeg,image/jpg,image/png,image/gif"
                  listType="picture-card"
                  showUploadList={false}
                  customRequest={async (options) => {
                    const { onSuccess, onError, file } = options;
                    console.log(
                      "📤 customRequest 触发，开始上传:",
                      (file as File).name,
                    );

                    // 使用 Hook 处理图片压缩
                    const fileToUpload = await handleBeforeUpload(file as File);
                    if (fileToUpload === false) {
                      // 用户取消上传
                      onError?.(new Error("User cancelled"));
                      return;
                    }

                    setIsPhotoUploading(true);
                    try {
                      const formData = new FormData();
                      formData.append("file", fileToUpload);
                      formData.append("module", "ORDER");
                      formData.append("key", orderNo || "");

                      // 调用上传接口获取OSS路径
                      const ossPath = await uploadFile(formData);

                      if (ossPath) {
                        setPhotoOssPath(ossPath);

                        // 使用本地 blob URL 作为预览（不调用 getFileBlob）
                        const previewUrl = URL.createObjectURL(fileToUpload);
                        setPhotoDisplayUrl(previewUrl);

                        message.success("照片上載成功");
                        console.log(
                          "✅ 上传成功，OSS路径:",
                          ossPath,
                          "本地预览URL:",
                          previewUrl,
                        );

                        // 返回成功
                        onSuccess?.({ ossPath, previewUrl }, fileToUpload);
                      } else {
                        message.error("照片上載失敗");
                        console.log("❌ 上載失敗");
                        onError?.(new Error("上載失敗"));
                      }
                    } catch (error) {
                      console.error("照片上載失敗:", error);
                      message.error("照片上載失敗");
                      onError?.(error as Error);
                    } finally {
                      setIsPhotoUploading(false);
                    }
                  }}
                  beforeUpload={(file) => {
                    const isImage = file.type.startsWith("image/");
                    if (!isImage) {
                      message.error("只能上載圖片檔案！");
                      return Upload.LIST_IGNORE;
                    }
                    const isLt10M = file.size / 1024 / 1024 < 10;
                    if (!isLt10M) {
                      message.error("圖片大小不能超過 10MB！");
                      return Upload.LIST_IGNORE;
                    }
                    return true;
                  }}
                  disabled={isPhotoUploading}
                >
                  {
                    <div>
                      {isPhotoUploading ? (
                        <LoadingOutlined />
                      ) : (
                        <PlusOutlined />
                      )}
                      <div style={{ marginTop: 8 }}>
                        {isPhotoUploading ? "上載中..." : "上載"}
                      </div>
                    </div>
                  }
                </Upload>
              )}
            </div>
          </div>

          {/* Step 3: Signature */}
          <div
            className="step-row"
            style={{ display: "flex", marginBottom: 0 }}
          >
            <div
              style={{
                marginRight: 16,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
              }}
            >
              <div
                style={{
                  width: 24,
                  height: 24,
                  borderRadius: "50%",
                  border: "2px solid #d9d9d9",
                  color: "#d9d9d9",
                  textAlign: "center",
                  lineHeight: "20px",
                  fontWeight: "bold",
                }}
              >
                3
              </div>
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 16, fontWeight: 500, marginBottom: 12 }}>
                03 簽字
              </div>
              <Space size={24} wrap>
                {/* 动态渲染签名框 */}
                {signatureTypes.map((type) => {
                  const dataKey =
                    SIGNATURE_TYPE_KEYS[type] ||
                    `${type.toLowerCase()}SignData`;
                  const displayName = signatureTypeNames[type] || type;
                  const hasSignature = !!signatures[dataKey];

                  return (
                    <div
                      key={type}
                      onClick={() => handleOpenSignaturePad(type)}
                      style={{
                        width: 140,
                        height: 140,
                        border: hasSignature
                          ? "2px solid var(--theme-primary)"
                          : "1px dashed #d9d9d9",
                        borderRadius: 4,
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        cursor: "pointer",
                        background: hasSignature ? "#fff" : "#fafafa",
                        position: "relative",
                        overflow: "hidden",
                      }}
                    >
                      {hasSignature ? (
                        <img
                          src={signatures[dataKey]}
                          alt={displayName}
                          style={{
                            width: "100%",
                            height: "100%",
                            objectFit: "contain",
                          }}
                        />
                      ) : (
                        <>
                          <EditOutlined
                            style={{
                              fontSize: 20,
                              color: "#999",
                              marginBottom: 4,
                            }}
                          />
                          <Text type="secondary" style={{ fontSize: 12 }}>
                            {displayName}
                          </Text>
                        </>
                      )}
                    </div>
                  );
                })}
              </Space>
            </div>
          </div>

          <div
            style={{
              textAlign: "right",
              marginTop: 40,
              display: "flex",
              justifyContent: "flex-end",
            }}
          >
            <CustomButton
              height={40}
              fontSize={16}
              bgColor="var(--theme-primary)"
              color="#fff"
              onClick={handleSubmit}
              width={120}
            >
              提交
            </CustomButton>
          </div>
        </Modal>

        {/* Actual Signing Modal (Nested) */}
        <Modal
          open={!!signingRole}
          title={
            signingRole
              ? signatureTypeNames[signingRole] || signingRole
              : "簽名"
          }
          footer={null}
          onCancel={() => {
            setSigningRole(null);
            setIsSignatureReady(false);
          }}
          width={800}
          centered
          maskClosable={false}
          destroyOnHidden
        >
          <div
            style={{
              padding: 16,
              background: "#FFFBE6",
              borderBottom: "1px solid #FFE58F",
              color: "#FAAD14",
              textAlign: "center",
            }}
          >
            <ExclamationCircleFilled /> 請使用正楷簽名
          </div>
          <div
            style={{
              padding: 24,
              display: "flex",
              justifyContent: "center",
              background: "#f0f2f5",
            }}
          >
            <div style={{ background: "#fff", border: "1px solid #e8e8e8" }}>
              <SignaturePad
                ref={signaturePadRef}
                width={700}
                height={400}
                onSave={handleSaveSignature}
                onContentChange={setIsSignatureReady}
                showControls={false}
                defaultValue={tempSignature}
                backgroundName={signingRole ? signatureNames[signingRole] : undefined}
              />
            </div>
          </div>
          <div
            style={{
              padding: "16px 24px",
              display: "flex",
              justifyContent: "space-between",
              borderTop: "1px solid #f0f0f0",
            }}
          >
            <CustomButton
              height={40}
              fontSize={16}
              bgColor="#fff"
              color="#666"
              style={{ border: "1px solid #d9d9d9" }}
              onClick={() => {
                setSigningRole(null);
                setIsSignatureReady(false);
              }}
            >
              簽署首頁
            </CustomButton>
            <Space>
              <CustomButton
                height={40}
                fontSize={16}
                bgColor="#fff"
                color="#666"
                style={{ border: "1px solid #d9d9d9" }}
                onClick={() => signaturePadRef.current?.clear()}
              >
                重寫
              </CustomButton>
              <CustomButton
                height={40}
                fontSize={16}
                bgColor="var(--theme-primary)"
                color="#fff"
                disabled={!isSignatureReady}
                onClick={() => signaturePadRef.current?.save()}
              >
                確認
              </CustomButton>
            </Space>
          </div>
        </Modal>

        {/* 照片预览 Modal */}
        <FilePreviewModal
          open={photoPreviewOpen}
          onClose={() => setPhotoPreviewOpen(false)}
          fileUrl={photoPreviewFile?.url}
          fileName={photoPreviewFile?.name}
          fileType="image"
        />
      </div>
    );
  },
);
